<template>
  <div class="stats-dashboard">
    <!-- Header Section with Title -->
    <div class="section-header mb-4" :class="{ 'gradient-loading': isLoading }">
      <h2 class="mb-1">
        <i class="fas fa-chart-line me-2"></i>
        Resumen de Rendimiento
      </h2>
      <p class="section-subtitle">Un vistazo detallado a tus números y métricas clave</p>
    </div>

    <!-- Period Selector -->
    <div class="mb-4 period-selector-container">
      <div class="d-flex pt-2 justify-content-between align-items-center flex-wrap gap-2">
        <h4 class="period-title">
          <i class="fas fa-chart-pie me-2"></i>
          Tus Números
        </h4>
        <div class="period-selector d-flex align-items-center flex-wrap">
          <div class="me-2 mb-2 mb-md-0 d-flex gap-2" role="group">
            <button 
              v-for="period in periods" 
              :key="period.value"
              @click="changePeriod(period.value)"
              class="gradient-btn"
              :class=" [
                selectedPeriod === period.value && !isCustomPeriod
                  ? 'gradient-btn-primary' 
                  : 'gradient-btn-outline'
              ]"
            >
              {{ period.label }}
            </button>
          </div>
          <button 
            @click="showDateRangePicker = !showDateRangePicker" 
            class="gradient-btn mb-2 mb-md-0"
            :class="[isCustomPeriod ? 'gradient-btn-primary' : 'gradient-btn-outline']"
          >
            <i class="fas fa-calendar-alt me-1"></i> Personalizar
          </button>
        </div>
      </div>
    </div>
    
    <!-- Date range picker -->
    <div v-if="showDateRangePicker" class="date-range-picker mb-4 animate__animated animate__fadeIn">
      <div class="gradient-card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-5 col-12">
              <label for="startDate" class="form-label">Desde</label>
              <input 
                type="date" 
                id="startDate" 
                class="gradient-form-control w-100" 
                v-model="customStartDate"
              />
            </div>
            <div class="col-md-5 col-12">
              <label for="endDate" class="form-label">Hasta</label>
              <input 
                type="date" 
                id="endDate" 
                class="gradient-form-control w-100" 
                v-model="customEndDate"
              />
            </div>
            <div class="col-md-2 col-12 d-flex align-items-end">
              <button 
                class="gradient-btn gradient-btn-primary w-100" 
                @click="applyCustomDateRange"
                :disabled="!isValidDateRange"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards: 2×2 en sm–md, 4 en fila desde lg -->
    <div class="row g-3 g-md-4 mb-4 stats-metrics-row px-2">
      <div class="col-12 col-sm-6 col-lg-3" v-for="(config, key) in statCards" :key="key">
        <div class="gradient-card" :class="{ 'gradient-loading': isLoading }">
          <div v-if="isLoading" class="skeleton-content">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text">
              <div class="skeleton-value"></div>
              <div class="skeleton-label"></div>
            </div>
          </div>
          <div v-else>
            <div class="stats-details">
              <div class="feature-icon" :style="{ backgroundColor: config.iconBackground, color: config.iconColor }">
                <i :class="config.icon"></i>
              </div>
              <div>
                <h4 class="stats-value">
                  <span v-if="config.isCurrency">$</span>
                  {{ formatValue(stats[config.value], config.isCurrency) }}
                </h4>
                <p class="stats-label">
                  {{ config.label }}
                  <i class="fas fa-info-circle text-muted ms-1" 
                     data-bs-toggle="tooltip" 
                     :title="config.tooltip"></i>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row g-3 g-lg-4 mb-4 align-items-stretch stats-charts-row">
      <!-- Revenue and Avg Ticket Chart -->
      <div class="col-lg-8 mb-4 mb-lg-0 d-flex">
        <div class="chart-gradient-card w-100" :class="{ 'gradient-loading': isLoading }">
          <div class="gradient-card-header">
            <h5>Facturación y Ticket Promedio</h5>
          </div>
          <div class="chart-body">
            <div v-if="isLoading" class="skeleton-chart">
              <div class="skeleton-chart-bars">
                <div v-for="n in 8" :key="n" class="skeleton-bar" 
                     :style="{ height: `${Math.floor(30 + Math.random() * 70)}px`}"></div>
              </div>
              <div class="skeleton-chart-labels">
                <div v-for="n in 8" :key="n" class="skeleton-label-item"></div>
              </div>
            </div>
            <canvas v-else ref="revenueChart" id="revenueChart" height="250"></canvas>
          </div>
        </div>
      </div>

      <!-- Customers Pie Chart -->
      <div class="col-lg-4 d-flex">
        <div class="chart-gradient-card w-100" :class="{ 'gradient-loading': isLoading }">
          <div class="gradient-card-header">
            <h5>Clientes Registrados</h5>
          </div>
          <div class="chart-body text-center">
            <div v-if="isLoading" class="skeleton-pie-chart">
              <div class="skeleton-pie-segment" style="transform: rotate(0deg) skew(30deg)"></div>
              <div class="skeleton-pie-segment" style="transform: rotate(120deg) skew(30deg)"></div>
              <div class="skeleton-pie-segment" style="transform: rotate(240deg) skew(30deg)"></div>
              <div class="skeleton-pie-center"></div>
            </div>
            <canvas v-else ref="customersChart" id="customersChart" height="250"></canvas>
          </div>
        </div>
      </div>
    </div>
    <!-- Productos más vendidos (mismo periodo) -->
    <div class="top-products-section">
      <div class="gradient-card" :class="{ 'gradient-loading': isLoading }">
        <div class="gradient-card-header top-products-header">
          <h5 class="mb-0 d-flex align-items-center gap-2">
            <i class="fas fa-trophy top-products-icon" aria-hidden="true"></i>
            Productos más vendidos
          </h5>
          <span class="top-products-hint">Unidades en pedidos del periodo (no cancelados ni abandonados)</span>
        </div>
        <div class="top-products-body">
          <div v-if="isLoading" class="skeleton-top-products">
            <div v-for="n in 6" :key="n" class="skeleton-top-row" />
          </div>
          <p v-else-if="!topSellingProducts.length" class="text-muted small mb-0 py-4 text-center">
            No hay ventas con productos en este periodo.
          </p>
          <ol v-else class="top-products-list list-unstyled mb-0">
            <li
              v-for="(row, idx) in topSellingProducts"
              :key="`${row.product_id}-${idx}`"
              class="top-products-row"
            >
              <span class="top-rank" :class="{ 'top-rank--gold': idx === 0, 'top-rank--silver': idx === 1, 'top-rank--bronze': idx === 2 }">{{ idx + 1 }}</span>
              <span class="top-name" :title="row.name">{{ row.name }}</span>
              <span class="top-stat top-units"><strong>{{ formatTopUnits(row.units_sold) }}</strong> u.</span>
              <span class="top-stat top-rev" title="Suma de precio×cantidad por línea">${{ formatValue(row.revenue, true) }}</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
    <!-- Performance Summary -->
    <div class="performance-summary mt-4">
      <div class="row">
        <div class="col-md-12">
          <div class="gradient-card" :class="{ 'gradient-loading': isLoading }">
            <h5 class="summary-title">Resumen de Rendimiento</h5>
            <div v-if="isLoading" class="skeleton-summary">
              <div class="skeleton-summary-text"></div>
              <div class="skeleton-summary-indicator"></div>
            </div>
            <div v-else class="d-flex justify-content-between align-items-center flex-wrap">
              <p class="summary-text mb-0">
                Durante este periodo, has logrado <strong>${{ formatValue(stats.facturacion, true) }}</strong> 
                en facturación con <strong>{{ stats.ventas }}</strong> ventas realizadas.
              </p>
              <div class="performance-indicator">
                <div class="indicator" :class="facturationIndicator > 0 ? 'text-success' : 'text-danger'">
                  <i :class="facturationIndicator > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
                  {{ Math.abs(facturationIndicator) }}%
                </div>
                <div class="period">vs. periodo anterior</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import Chart from 'chart.js/auto';
import '@/assets/styles/admin-components.css'; // Import common styles
import statisticsService from '@/services/statisticsService'; // Importar el servicio de estadísticas

// Datos y estados
const stats = ref({
  ventas: 0,
  facturacion: 0,
  ticketPromedio: 0,
  usuarios: 0
});

/** Top filas desde GET /products/orders/top-selling-products/ */
const topSellingProducts = ref([]);

// Referencias para los charts
const revenueChart = ref(null);
const customersChart = ref(null);
let revChart = null;
let custChart = null;

/** Último payload de stats usado para recrear el gráfico de facturación */
const lastRevenueStats = ref(null);

// Estado de carga
const isLoading = ref(true);

// Configuración de período
const selectedPeriod = ref('month');
const isCustomPeriod = ref(false);
const showDateRangePicker = ref(false);
const customStartDate = ref('');
const customEndDate = ref('');
const facturationIndicator = ref(12); // Valor simulado para el indicador de facturación

// Definir los períodos disponibles
const periods = [
  { value: 'day', label: 'Hoy' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' }
];

// Computed property to validate date range
const isValidDateRange = computed(() => {
  if (!customStartDate.value || !customEndDate.value) return false;
  
  const start = new Date(customStartDate.value);
  const end = new Date(customEndDate.value);
  
  return start <= end;
});

// Actualizar la configuración de las tarjetas de estadísticas
const statCards = {
  'Usuarios': {
    label: 'Usuarios registrados',
    value: 'usuarios',
    icon: 'fas fa-user-plus',
    iconBackground: 'rgba(var(--success-color-rgb), 0.2)',
    iconColor: 'var(--primary-color)',
    tooltip: 'Número total de usuarios registrados',
    isCurrency: false,
    change: 0
  },
  'Ventas': {
    label: 'Ventas',
    value: 'ventas',
    icon: 'fas fa-shopping-cart',
    iconBackground: 'rgba(111, 66, 193, 0.2)',
    iconColor: '#6f42c1',
    tooltip: 'Número total de ventas realizadas',
    isCurrency: false,
    change: 0
  },
  'Facturación': {
    label: 'Facturación',
    value: 'facturacion',
    icon: 'fas fa-dollar-sign',
    iconBackground: 'rgba(23, 162, 184, 0.2)',
    iconColor: '#17a2b8',
    tooltip: 'Importe total facturado',
    isCurrency: true,
    change: 0
  },
  'Ticket promedio': {
    label: 'Ticket promedio',
    value: 'ticketPromedio',
    icon: 'fas fa-receipt',
    iconBackground: 'rgba(255, 193, 7, 0.2)',
    iconColor: '#ffc107',
    tooltip: 'Valor promedio de cada venta',
    isCurrency: true,
    change: 0
  }
};

// Formatear valores
const formatValue = (value, isCurrency) => {
  if (!value && value !== 0) return '0';
  
  if (isCurrency) {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  
  return value.toLocaleString('es-AR');
};

const formatTopUnits = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '0';
  return Number(n).toLocaleString('es-AR');
};

const commonRevenueScales = {
  x: {
    offset: false,
    grid: { color: 'rgba(255, 255, 255, 0.1)' },
    ticks: { color: 'rgba(255, 255, 255, 0.7)', maxRotation: 45, minRotation: 0 }
  },
  y: {
    beginAtZero: true,
    grace: '12%',
    grid: { color: 'rgba(255, 255, 255, 0.1)' },
    ticks: {
      color: 'rgba(255, 255, 255, 0.7)',
      callback: function (value) {
        return '$' + formatValue(value, true);
      }
    }
  }
};

const commonRevenuePlugins = {
  legend: {
    position: 'top',
    labels: { color: '#e1e1e1', font: { size: 12 } }
  },
  tooltip: {
    mode: 'index',
    intersect: false,
    callbacks: {
      label: (context) => {
        let label = context.dataset.label || '';
        if (label) label += ': ';
        const y = context.parsed.y;
        if (y != null) label += '$' + formatValue(y, true);
        return label;
      }
    }
  }
};

/**
 * Crea o reemplaza el gráfico de facturación / ticket en líneas.
 */
const createRevenueChart = (initialData) => {
  if (!initialData) return;
  if (!revenueChart.value) return;

  if (revChart) {
    revChart.destroy();
    revChart = null;
  }

  const timelineData = initialData.timeline || [];
  const labels = timelineData.length
    ? timelineData.map((item) => item.date)
    : ['—'];
  const factData = timelineData.length ? timelineData.map((item) => item.value) : [0];
  const ticketData = timelineData.length
    ? timelineData.map((item) => (item.orders > 0 ? item.value / item.orders : 0))
    : [0];
  const singlePoint = labels.length < 2;
  const pointR = singlePoint ? 6 : 3;

  const datasets = [
    {
      type: 'line',
      label: 'Facturación',
      data: factData,
      borderColor: 'rgba(111, 66, 193, 1)',
      backgroundColor: 'rgba(111, 66, 193, 0.12)',
      borderWidth: 3,
      fill: false,
      tension: 0,
      pointRadius: pointR,
      pointHoverRadius: pointR + 2,
      pointBackgroundColor: 'rgba(111, 66, 193, 1)',
      pointBorderColor: 'rgba(255, 255, 255, 0.9)',
      pointBorderWidth: 2,
      spanGaps: true
    },
    {
      type: 'line',
      label: 'Ticket Promedio',
      data: ticketData,
      borderColor: 'rgba(23, 162, 184, 1)',
      backgroundColor: 'rgba(23, 162, 184, 0.1)',
      borderWidth: 3,
      fill: false,
      tension: 0,
      pointRadius: pointR,
      pointHoverRadius: pointR + 2,
      pointBackgroundColor: 'rgba(23, 162, 184, 1)',
      pointBorderColor: 'rgba(255, 255, 255, 0.9)',
      pointBorderWidth: 2,
      spanGaps: true
    }
  ];

  const revCtx = revenueChart.value.getContext('2d');
  revChart = new Chart(revCtx, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: commonRevenuePlugins,
      scales: commonRevenueScales,
      elements: {
        line: { borderJoinStyle: 'round' }
      }
    }
  });
};

// Crear y actualizar los gráficos
const createCharts = async (initialData) => {
  if (!initialData) {
    console.error("No hay datos disponibles para los gráficos");
    return;
  }

  lastRevenueStats.value = initialData;

  try {
    await nextTick();
    
    if (!revenueChart.value || !customersChart.value) {
      // Si los canvas todavía no montaron, reintentar una vez tras el próximo tick.
      setTimeout(() => createCharts(initialData), 300);
      return;
    }
    
    if (custChart) {
      custChart.destroy();
      custChart = null;
    }

    createRevenueChart(initialData);

    const customers = initialData?.customers || {};
    const totalCustomers = Math.max(Number(customers.total) || 0, 0);
    const newCustomersRaw = Math.max(Number(customers.new) || 0, 0);
    const newCustomers = Math.min(newCustomersRaw, totalCustomers);
    const existingCustomers = Math.max(totalCustomers - newCustomers, 0);
    // Chart.js no dibuja dona con [0, 0], forzamos un mínimo visual sin alterar etiquetas.
    const doughnutValues =
      totalCustomers === 0 ? [1, 0] : [newCustomers, existingCustomers];

    const custCtx = customersChart.value.getContext('2d');
    // Creación del gráfico de clientes
    custChart = new Chart(custCtx, {
      type: 'doughnut',
      data: {
        labels: ['Clientes Nuevos', 'Clientes Existentes'],
        datasets: [{
          data: doughnutValues,
          backgroundColor: [
            totalCustomers === 0 ? 'rgba(148, 163, 184, 0.75)' : 'rgba(34, 197, 94, 0.85)',
            'rgba(59, 130, 246, 0.85)'
          ],
          borderColor: [
            totalCustomers === 0 ? 'rgba(203, 213, 225, 1)' : 'rgba(74, 222, 128, 1)',
            'rgba(96, 165, 250, 1)'
          ],
          borderWidth: 2,
          hoverOffset: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: '#e1e1e1',
              padding: 20,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const isEmptyState = totalCustomers === 0;
                const value = isEmptyState
                  ? 0
                  : label === 'Clientes Nuevos'
                    ? newCustomers
                    : existingCustomers;
                const percentage = isEmptyState
                  ? 0
                  : Math.round((value / totalCustomers) * 100);
                return `${label}: ${percentage}% (${value})`;
              }
            }
          }
        }
      }
    });
  } catch (error) {
    console.error("Error al crear los gráficos:", error);
  }
};

// Cargar datos
const loadData = async (isCustomRange = false) => {
  isLoading.value = true;
  const start = isCustomRange ? customStartDate.value : null;
  const end = isCustomRange ? customEndDate.value : null;
  const period = selectedPeriod.value;

  try {
    const [response, topRes] = await Promise.all([
      statisticsService.getStatistics(period, start, end),
      statisticsService.getTopSellingProducts(period, start, end).catch((err) => {
        console.warn('Top selling products:', err);
        return { top_products: [] };
      })
    ]);

    stats.value = {
      ...response.stats,
      usuarios: response.stats.customers?.total || 0
    };
    topSellingProducts.value = Array.isArray(topRes.top_products) ? topRes.top_products : [];

    if (response.stats.changes) {
      statCards['Ventas'].change = response.stats.changes.ventas;
      statCards['Facturación'].change = response.stats.changes.facturacion;
    }

    // Primero renderizamos los canvas (apagando skeleton), luego creamos charts.
    isLoading.value = false;
    await nextTick();
    createCharts(response.stats);
  } catch (error) {
    console.error('Error loading statistics data:', error);
    topSellingProducts.value = [];
    isLoading.value = false;
  }
};

// Métodos para manejar el cambio de período
const changePeriod = async (period) => {
  selectedPeriod.value = period;
  isCustomPeriod.value = false;
  showDateRangePicker.value = false;
  console.log(`Cambiando al período: ${period}`);
  
  // El loadData maneja el estado de isLoading
  await loadData();
};

const applyCustomDateRange = async () => {
  if (!isValidDateRange.value) return;
  
  isCustomPeriod.value = true;
  showDateRangePicker.value = false;
  
  console.log(`Aplicando rango personalizado: ${customStartDate.value} - ${customEndDate.value}`);
  
  // El loadData maneja el estado de isLoading con isCustomRange=true
  await loadData(true);
};

onMounted(async () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  customEndDate.value = today.toISOString().split('T')[0];
  customStartDate.value = firstDayOfMonth.toISOString().split('T')[0];
  await loadData(false);
});

// Ver si cambiamos el tamaño de la ventana
watch(() => [window.innerWidth, window.innerHeight], () => {
  // Verificar si los gráficos existen y tienen el método resize
  if (revChart && typeof revChart.resize === 'function') {
    revChart.resize();
  } else if (revChart) {
    revChart.update();  // Si no tiene resize, intentamos con update
  }
  
  if (custChart && typeof custChart.resize === 'function') {
    custChart.resize();
  } else if (custChart) {
    custChart.update();  // Si no tiene resize, intentamos con update
  }
});
</script>

<style scoped>
/* ===== Dashboard Container ===== */
/* Un solo gutter horizontal: bordes de cards alineados en todas las secciones */
.stats-dashboard {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem 1.5rem;
}
@media (min-width: 992px) {
  .stats-dashboard {
    padding: 1rem 1.5rem 2rem;
  }
}

/* Grid de métricas: 4 columnas en lg, misma altura */
.stats-metrics-row {
  align-items: stretch;
  --bs-gutter-x: 1rem;
  --bs-gutter-y: 1rem;
}
@media (min-width: 768px) {
  .stats-metrics-row {
    --bs-gutter-x: 1.25rem;
    --bs-gutter-y: 1.25rem;
  }
}
.stats-metrics-row > [class*='col-'] {
  display: flex;
  flex-direction: column;
}
.stats-metrics-row .gradient-card {
  flex: 1 1 auto;
  width: 100%;
  min-height: 5.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Gráficos: misma altura y ancho alineado con métricas */
.stats-charts-row {
  --bs-gutter-x: 1rem;
  --bs-gutter-y: 1rem;
}
@media (min-width: 992px) {
  .stats-charts-row {
    --bs-gutter-x: 1.25rem;
  }
}
.stats-charts-row > [class*='col-'] {
  display: flex;
  flex-direction: column;
}
.stats-charts-row .chart-gradient-card {
  flex: 1 1 auto;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
.stats-charts-row .chart-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}

/* ===== Section Header ===== */
.section-subtitle {
  font-size: 0.82rem;
  color: #555;
  margin-bottom: 0;
}

/* ===== Period Selector ===== */
.period-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ccc;
  margin-bottom: 0.75rem;
  margin-right: 0.75rem;
}

/* ===== Stats Cards ===== */
.stats-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.stats-value {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 0.15rem;
  color: #ccc;
  text-align: right;
  letter-spacing: -0.3px;
}

.stats-label {
  margin-bottom: 0;
  font-size: 0.78rem;
  color: #666;
  text-align: right;
  letter-spacing: 0.02em;
}

.stats-label .fa-info-circle {
  color: #444 !important;
  font-size: 0.7rem;
}

/* ===== Chart Section ===== */
.chart-body {
  padding: 1rem;
  position: relative;
}

/* ===== Top selling products ===== */
.top-products-header {
  flex-direction: column;
  align-items: flex-start !important;
  gap: 0.25rem;
}
@media (min-width: 576px) {
  .top-products-header {
    flex-direction: row;
    align-items: center !important;
    justify-content: space-between;
  }
}
.top-products-icon {
  color: #d4a017;
  font-size: 0.95rem;
}
.top-products-hint {
  font-size: 0.72rem;
  color: #666;
  line-height: 1.3;
  max-width: 22rem;
}
.top-products-body {
  padding: 0.75rem 1rem 1rem;
}
.top-products-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.top-products-row {
  display: grid;
  grid-template-columns: 2.25rem 1fr auto auto;
  align-items: center;
  gap: 0.5rem 0.75rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.84rem;
  color: #c4c4c4;
}
@media (max-width: 575px) {
  .top-products-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
  }
  .top-name {
    flex: 1 1 calc(100% - 3rem);
    white-space: normal;
  }
  .top-rev { margin-left: auto; }
}
.top-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
}
.top-rank--gold { color: #1a1a1a; background: linear-gradient(145deg, #e8c547, #c9a227); }
.top-rank--silver { color: #222; background: linear-gradient(145deg, #d8d8d8, #a8a8a8); }
.top-rank--bronze { color: #2a1810; background: linear-gradient(145deg, #cd7f32, #a5662a); }
.top-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #ddd;
}
.top-stat {
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
}
.top-units strong { color: var(--primary-light); }
.top-rev {
  font-variant-numeric: tabular-nums;
  min-width: 5rem;
  text-align: right;
}
.skeleton-top-products {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.skeleton-top-row {
  height: 2.5rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  animation: pulse 1.5s infinite ease-in-out;
}

.top-products-section {
  margin-top: 0.25rem;
}

/* ===== Performance Summary ===== */
.summary-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #ccc;
  margin-bottom: 0.75rem;
}

.summary-text {
  color: #888;
  line-height: 1.7;
  font-size: 0.84rem;
}

.summary-text strong {
  color: #ccc;
  font-weight: 600;
}

.performance-indicator {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.indicator {
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.indicator.text-success { color: var(--success-color) !important; }
.indicator.text-danger { color: #f87171 !important; }

.period {
  font-size: 0.72rem;
  color: #555;
}

/* ===== Form Label ===== */
.form-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.25rem;
}

/* ===== Skeleton Loading ===== */
.skeleton-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.skeleton-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-text {
  width: 60%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.skeleton-value {
  height: 22px;
  width: 80%;
  background: rgba(255,255,255,0.06);
  margin-bottom: 6px;
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-label {
  height: 14px;
  width: 60%;
  background: rgba(255,255,255,0.06);
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

/* Skeleton Chart */
.skeleton-chart {
  height: 250px;
  padding: 1.25rem 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.skeleton-chart-bars {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 85%;
}

.skeleton-bar {
  width: 10%;
  background: rgba(255,255,255,0.06);
  border-radius: 4px 4px 0 0;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-chart-labels {
  display: flex;
  justify-content: space-around;
  margin-top: 0.65rem;
  height: 15%;
}

.skeleton-label-item {
  width: 10%;
  height: 8px;
  background: rgba(255,255,255,0.06);
  border-radius: 4px;
  animation: pulse 1.5s infinite ease-in-out;
}

/* Skeleton Pie Chart */
.skeleton-pie-chart {
  position: relative;
  width: 170px;
  height: 170px;
  margin: 1.25rem auto;
  border-radius: 50%;
  overflow: hidden;
}

.skeleton-pie-segment {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.06);
  transform-origin: center;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-pie-center {
  position: absolute;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  background: #1c1c1c;
  border-radius: 50%;
}

.skeleton-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skeleton-summary-text {
  height: 18px;
  width: 70%;
  background: rgba(255,255,255,0.06);
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-summary-indicator {
  height: 18px;
  width: 20%;
  background: rgba(255,255,255,0.06);
  border-radius: 6px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 0.3; }
  100% { opacity: 0.6; }
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .stats-dashboard { padding: 0.75rem 0.75rem 1.25rem; }
  .stats-value { font-size: 1rem; }
  .stats-label { font-size: 0.72rem; }
  .period-title { font-size: 0.87rem; }
  .chart-body { padding: 0.75rem; }
}
</style>
